import React, {useMemo} from "react";
import {IconButton, Stack, TableCell, Tooltip, Typography} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PublicIcon from "@mui/icons-material/Public";
import BusinessIcon from "@mui/icons-material/Business";
import {DataTable} from "../../components/data-table/DataTable";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {HibobOffice, WorkSite} from "../../api/Model";
import {SiteChip} from "../../components/site-chip/SiteChip";
import {groupBy} from "../../util/MapUtil";

export type HibobResidenceRow =
    | { id: string, kind: 'site', site: WorkSite | null }
    | { id: string, kind: 'office', office: HibobOffice, site: WorkSite | null };

export function hibobResidenceRows(sites: WorkSite[], offices: HibobOffice[]): HibobResidenceRow[] {
    const siteIds = new Set(sites.map(site => site.id));
    const officesBySite = groupBy(offices, office =>
        office.workSiteId !== null && siteIds.has(office.workSiteId) ? office.workSiteId : null);
    const rows: HibobResidenceRow[] = [];
    const addSite = (site: WorkSite | null) => {
        rows.push({id: `site-${site?.id ?? 'undefined'}`, kind: 'site', site});
        for (const office of officesBySite.get(site?.id ?? null) ?? []) {
            rows.push({id: `office-${office.id}`, kind: 'office', office, site});
        }
    };
    sites.forEach(addSite);
    if (officesBySite.has(null)) addSite(null);
    return rows;
}

export function HibobResidenceTable({rows, onEditSite, onEditOffice}: {
    rows: HibobResidenceRow[],
    onEditSite?: (site: WorkSite) => void,
    onEditOffice?: (office: HibobOffice) => void,
}) {
    const columns = useMemo<TableColumnData<HibobResidenceRow>[]>(() => [
        {
            key: 'name',
            header: 'Name',
            cell: row => (
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" pl={row.kind === 'office' ? 3 : 0}>
                        {row.kind === 'office' ? (
                            <BusinessIcon color="action"/>
                        ) : row.site?.emoji ? (
                            <Typography variant="h5">{row.site.emoji}</Typography>
                        ) : (
                            <PublicIcon color="action"/>
                        )}
                        <span>{row.kind === 'site' ? (
                            row.site?.name ?? 'Undefined Work Site'
                        ) : (
                            row.office.name
                        )}</span>
                    </Stack>
                </TableCell>
            ),
        },
        {
            key: 'badge',
            header: 'Badge',
            width: '10em',
            cell: row => (
                <TableCell>
                    <SiteChip siteId={row.site?.id} hibobOfficeId={row.kind === 'office' ? row.office.id : undefined}/>
                </TableCell>
            ),
        },
        {
            key: 'actions',
            header: 'Actions',
            width: '8em',
            align: 'right',
            cell: row => {
                const onEdit = row.kind === 'site'
                    ? row.site && onEditSite && (() => onEditSite(row.site!))
                    : onEditOffice && (() => onEditOffice(row.office));
                const label = row.kind === 'site' ? `Edit work site ${row.site?.name}` : `Edit Hibob office ${row.office.name}`;
                return (
                    <TableCell align="right">
                        {onEdit && (
                            <Tooltip title={label}>
                                <IconButton color="primary" aria-label={label} onClick={onEdit}>
                                    <EditIcon/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </TableCell>
                );
            },
        },
    ], [onEditSite, onEditOffice]);

    return <DataTable id={row => row.id} columns={columns} rows={rows}/>;
}
